import { useState } from 'react';
import { Alert, Button, Card, Eyebrow, Field, Input, Select, formatDateTime, money, useConfirm } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer, StatusBadge } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useT } from '../i18n/index.js';

/** Booking search and detail (docs/06 "/bookings"). */

interface BookingRow {
  id: string;
  reference: string;
  status: string;
  contactName: string;
  contactEmail: string;
  totalCents: number;
  currency: string;
  createdAt: string;
  seats: Array<{ seatCode: string; passengerName: string }>;
  trip: { departureAt: string; status: string; route: { name: string } };
}

interface BookingDetail extends BookingRow {
  refundedCents: number;
  commissionCents: number;
  seats: Array<{
    seatCode: string;
    passengerName: string;
    priceCents: number;
    ticket: { qrToken: string; checkedInAt: string | null } | null;
  }>;
  payments: Array<{
    provider: string;
    providerRef: string;
    status: string;
    amountCents: number;
    refundedCents: number;
  }>;
  disputes: Array<{ id: string; subject: string; status: string }>;
  trip: BookingRow['trip'] & { bus: { name: string } };
}

export function Bookings() {
  const t = useT();
  const { tenant, user } = useSession();
  const timezone = tenant?.timezone ?? 'UTC';
  const [query, setQuery] = useState('');
  const [applied, setApplied] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const params = new URLSearchParams({ pageSize: '50' });
  if (applied) params.set('query', applied);
  if (status) params.set('status', status);
  const bookings = useResource<BookingRow[]>(`/admin/bookings?${params.toString()}`);

  const columns: Column<BookingRow>[] = [
    {
      key: 'reference',
      header: t('f.reference'),
      render: (row) => <span className="tabular font-medium">{row.reference}</span>,
    },
    {
      key: 'passenger',
      header: t('f.passenger'),
      render: (row) => (
        <div>
          <p>{row.contactName}</p>
          <p className="text-xs" style={{ color: 'var(--color-faint)' }}>
            {row.contactEmail}
          </p>
        </div>
      ),
    },
    {
      key: 'trip',
      header: t('f.departure'),
      render: (row) => (
        <div>
          <p>{row.trip.route.name}</p>
          <p className="text-xs tabular" style={{ color: 'var(--color-muted)' }}>
            {formatDateTime(row.trip.departureAt, timezone)}
          </p>
        </div>
      ),
    },
    {
      key: 'seats',
      header: t('f.seats'),
      render: (row) => (
        <span className="tabular">{row.seats.map((s) => s.seatCode).join(', ') || '—'}</span>
      ),
    },
    {
      key: 'total',
      header: t('f.total'),
      numeric: true,
      render: (row) => money(row.totalCents, row.currency),
    },
    { key: 'status', header: t('f.status'), render: (row) => <StatusBadge status={row.status} /> },
  ];

  return (
    <Page title={t('nav.bookings')} description={t('bk.searchHint')}>
      <form
        className="mb-4 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(query.trim());
        }}
      >
        <Field label={t('f.search')} htmlFor="b-query">
          <Input
            id="b-query"
            className="w-72"
            placeholder={t('bk.searchPh')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Field>
        <Field label={t('f.status')} htmlFor="b-status">
          <Select id="b-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any</option>
            <option value="CONFIRMED">{t('st.confirmed')}</option>
            <option value="PENDING_PAYMENT">{t('st.awaiting')}</option>
            <option value="CANCELLED">{t('st.cancelled')}</option>
            <option value="REFUNDED">{t('st.refunded')}</option>
            <option value="EXPIRED">{t('st.expired')}</option>
          </Select>
        </Field>
        <Button type="submit">{t('f.search')}</Button>
        {(applied || status) && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setQuery('');
              setApplied('');
              setStatus('');
            }}
          >
            {t('f.clear')}
          </Button>
        )}
      </form>

      <DataTable
        columns={columns}
        rows={bookings.data}
        rowKey={(row) => row.id}
        loading={bookings.loading}
        error={bookings.error}
        onRowClick={(row) => setSelected(row.id)}
        empty={{
          title: applied ? 'No bookings match that search' : 'No bookings yet',
          description: applied ? 'Try a reference, an email address or a passenger name.' : undefined,
        }}
      />

      {selected && (
        <BookingDrawer
          bookingId={selected}
          timezone={timezone}
          canRefund={user?.capabilities.includes('refunds:issue') ?? false}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            await bookings.reload();
          }}
        />
      )}
    </Page>
  );
}

function BookingDrawer({
  bookingId,
  timezone,
  canRefund,
  onClose,
  onChanged,
}: {
  bookingId: string;
  timezone: string;
  canRefund: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const t = useT();
  const booking = useResource<BookingDetail>(`/admin/bookings/${bookingId}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  async function refund() {
    if (!booking.data) return;
    const confirmed = await ask({
      title: `Refund ${money(booking.data.totalCents, booking.data.currency)} to ${booking.data.contactName}?`,
      body: 'Their seats are released and the money goes back to their card.',
      confirmLabel: t('bk.refund'),
    });
    if (!confirmed) return;

    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/bookings/${bookingId}/refund`, {});
      await booking.reload();
      await onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not refund this booking.'));
    } finally {
      setBusy(false);
    }
  }

  const data = booking.data;
  const refundable = data?.status === 'CONFIRMED';

  return (
    <Drawer open title={t('f.booking')} onClose={onClose}>
      {booking.loading && <p style={{ color: 'var(--color-muted)' }}>Loading…</p>}
      {booking.error && <Alert tone="error">{booking.error}</Alert>}
      {data && (
        <div className="flex flex-col gap-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="tabular text-lg font-semibold">{data.reference}</p>
              <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                Booked {formatDateTime(data.createdAt, timezone)}
              </p>
            </div>
            <StatusBadge status={data.status} />
          </div>

          <Card>
            <Eyebrow>{t('f.journey')}</Eyebrow>
            <p className="mt-1.5 font-medium">{data.trip.route.name}</p>
            <p className="text-sm tabular" style={{ color: 'var(--color-muted)' }}>
              {formatDateTime(data.trip.departureAt, timezone)} · {data.trip.bus.name}
            </p>
          </Card>

          <div>
            <Eyebrow>{t('f.passengers')}</Eyebrow>
            <ul className="mt-2 flex flex-col gap-1.5">
              {data.seats.map((seat) => (
                <li key={seat.seatCode} className="flex items-baseline justify-between text-sm">
                  <span>
                    <span className="tabular font-semibold">{seat.seatCode}</span> ·{' '}
                    {seat.passengerName}
                  </span>
                  <span style={{ color: seat.ticket?.checkedInAt ? 'var(--signal-departed)' : 'var(--color-faint)' }}>
                    {seat.ticket?.checkedInAt
                      ? `checked in ${formatDateTime(seat.ticket.checkedInAt, timezone)}`
                      : 'not checked in'}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <Eyebrow>{t('f.payment')}</Eyebrow>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('f.total')}</dt>
                <dd className="money font-medium">{money(data.totalCents, data.currency)}</dd>
              </div>
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('st.refunded')}</dt>
                <dd className="money font-medium">{money(data.refundedCents, data.currency)}</dd>
              </div>
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('bk.commission')}</dt>
                <dd className="money font-medium">{money(data.commissionCents, data.currency)}</dd>
              </div>
              <div>
                <dt style={{ color: 'var(--color-faint)' }}>{t('f.provider')}</dt>
                <dd className="font-medium">{data.payments[0]?.provider ?? '—'}</dd>
              </div>
            </dl>
          </div>

          {data.disputes.length > 0 && (
            <Alert tone="warning" title={t('bk.openRequest')}>
              {data.disputes.map((dispute) => (
                <p key={dispute.id}>
                  {dispute.subject} — {dispute.status.replace(/_/g, ' ').toLowerCase()}
                </p>
              ))}
            </Alert>
          )}

          {error && <Alert tone="error">{error}</Alert>}

          {refundable &&
            (canRefund ? (
              <Button variant="danger" loading={busy} onClick={refund}>
                {t('bk.refund')}
              </Button>
            ) : (
              <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                {t('bk.noRefundRole')}
              </p>
            ))}
        </div>
      )}
      {dialog}
    </Drawer>
  );
}
